import moment from "moment";
import { memo } from "react";

import { BTStatusBadge } from "@btnet/core-components";

import { isInPortal } from "utilities/portal/portal";
import { routes } from "utilities/routes";

import { BTIconExclamationCircleOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { LinkedScheduleItemStatusTag } from "commonComponents/entity/linkedScheduleItem/LinkedScheduleItemStatusTag";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { RelatedItemTag } from "commonComponents/entity/relatedItem/RelatedItemTag/RelatedItemTag";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { type IGridCellRendererParams } from "commonComponents/utilities/AgGridReact/AgGridReact.types";
import { DueDate } from "commonComponents/utilities/DueDate/DueDate";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { BillAccountingStatusTag } from "entity/bill/common/BillAccountingStatusTag/BillAccountingStatusTag";
import { BillStatusTag } from "entity/bill/common/BillStatus/BillStatus";
import { LienWaiverStatusTag } from "entity/lienWaiver/common/LienWaiverStatus/LienWaiverStatus";

import { getApproverStatusDisplayType, getApproverStatusIcon } from "./billAgCellRenderers.utils";
import {
    type IBillAmountFooterValue,
    type IBillGridRow,
    type IBillLinkResponse,
} from "./billAgRow.types";

/*
 * AG Grid cell renderers for Bill-specific columns (ado-281381), ported from the Kendo `Cell`
 * renderers in `commonComponents/utilities/Grid/bill/columnImplementations`.
 *
 * The grid is typed `TData = IBillGridRow`, so each renderer reads its column value from the typed
 * `params.data.<jsonKey>` instead of casting `params.value`. Values are the RAW server shapes
 * (client-data mode uses transformEntityAgGrid, which does not run the Kendo data mappers), so each
 * renderer maps its own value. `data-testid` uses the column field + row index (replaces Kendo's
 * `field.full`).
 */

type BillCellParams = IGridCellRendererParams<IBillGridRow>;

const testId = (params: BillCellParams): string => {
    // rowIndex is null on the pinned bottom (footer) row; use a stable "footer" suffix so its
    // testid stays unique instead of collapsing to a trailing-dot duplicate.
    const rowKey = params.node.rowPinned === "bottom" ? "footer" : (params.node.rowIndex ?? "");
    return `ag-grid-col-${params.colDef?.field ?? ""}.${rowKey}`;
};

/**
 * Bill link cell. `useAbsoluteLink` (from the `billsFullPageConversion` flag) selects a
 * standalone-page link vs. a relative modal-overlay link, matching the Kendo behavior. Serves
 * both the Bill # and Bill title columns, reading whichever field this column maps to.
 */
export const makeBillLinkCell = (useAbsoluteLink: boolean) =>
    memo(function BillLinkCell(params: BillCellParams) {
        const field = params.colDef?.field as "billNumberLink" | "billTitleLink" | undefined;
        const cellValue: IBillLinkResponse | null = field ? (params.data?.[field] ?? null) : null;
        if (!cellValue) {
            return null;
        }

        const link = isInPortal({ owner: true }) ? (
            <BTLink
                shouldOpenNewTab
                useAutoSPARouting
                to={routes.bill.getPrintLink([cellValue.id], cellValue.jobId)}
            >
                <TextTruncate>{cellValue.title}</TextTruncate>
            </BTLink>
        ) : useAbsoluteLink ? (
            <BTLink
                to={routes.bill.getDetailsLink(cellValue.id, cellValue.jobId)}
                data-testid={testId(params)}
            >
                <TextTruncate>{cellValue.title}</TextTruncate>
            </BTLink>
        ) : (
            <BTLinkRelative
                to={routes.bill.getDetailsPath(cellValue.id, cellValue.jobId)}
                data-testid={testId(params)}
            >
                <TextTruncate>{cellValue.title}</TextTruncate>
            </BTLinkRelative>
        );

        // h-full + flex items-center centers the link vertically in the AG Grid cell. Tailwind
        // utilities (layer: utilities) are used instead of a .less rule because AG Grid's
        // runtime-injected theme CSS outranks the app layer, so a .less rule wouldn't apply.
        return (
            <div className="flex items-center h-full">
                {/* min-w-0 lets the link truncate so the trailing duplicate icon keeps its space
                    (flex-shrink-0) and never gets clipped at the cell edge. */}
                <span className="min-w-0">{link}</span>
                {cellValue.isDuplicate && (
                    <BTIconExclamationCircleOutlined
                        className="padding-left-xs flex-shrink-0"
                        title="This bill could be a duplicate"
                    />
                )}
            </div>
        );
    });

export const BillAmountCell = memo(function BillAmountCell(params: BillCellParams) {
    // Pinned bottom (footer) row: value comes from `footerData` as { value, scale } (not a
    // IBillGridRow), so read params.value there. Regular rows read the typed row field.
    if (params.node.rowPinned === "bottom") {
        const footerValue = params.value as IBillAmountFooterValue | null | undefined;
        if (!footerValue || footerValue.value == null) {
            return null;
        }
        return (
            <div data-testid={testId(params)} className="flex flex-col items-end">
                <CurrencyDisplay value={footerValue.value} decimalScale={footerValue.scale} />
            </div>
        );
    }

    const cellValue = params.data?.paymentAmount;
    if (!cellValue || cellValue.value == null) {
        return null;
    }
    return (
        <div data-testid={testId(params)} className="flex flex-col items-end">
            <CurrencyDisplay value={cellValue.value} decimalScale={cellValue.scale} />
            {cellValue.isPartiallyPaid && (
                <span className="text-xs text-fg-tertiary">Partially paid</span>
            )}
        </div>
    );
});

/**
 * Renders any currency-typed column (Amount paid, Remaining balance, and currency custom fields).
 * The server sends these as a `{ value, scale }` object and transformEntityAgGrid does not flatten
 * it, so we read `.value` ourselves rather than passing the object to CurrencyDisplay (which expects
 * a number and throws a DecimalError on an object). Works for any column: reads the value from
 * `params.value` (AG Grid resolves it from the column's field for both data rows and the pinned
 * footer). Defensively unwraps whether the value is the `{ value, scale }` object or already a
 * number, so it can't crash the grid regardless of the raw shape.
 */
export const BillCurrencyCell = memo(function BillCurrencyCell(params: BillCellParams) {
    const raw = params.value as IBillAmountFooterValue | number | null | undefined;
    const amount = typeof raw === "object" && raw !== null ? raw.value : raw;
    const scale = typeof raw === "object" && raw !== null ? raw.scale : undefined;
    if (amount == null) {
        return null;
    }
    return (
        <div data-testid={testId(params)} className="flex flex-col items-end">
            <CurrencyDisplay value={amount} decimalScale={scale} />
        </div>
    );
});

export const BillPayToCell = memo(function BillPayToCell(params: BillCellParams) {
    const cellValue = params.data?.payTo;
    if (!cellValue) {
        return null;
    }
    return (
        <div className="billPayToCell flex align-items-center" data-testid={testId(params)}>
            <TextTruncate>{cellValue.payTo}</TextTruncate>
        </div>
    );
});

export const BillStatusCell = memo(function BillStatusCell(params: BillCellParams) {
    const cellValue = params.data?.purchaseOrderPaymentStatus;
    if (!cellValue || cellValue.paymentStatus === undefined) {
        return null;
    }
    return (
        <div className="flex items-center h-full" data-testid={testId(params)}>
            <BillStatusTag
                isSubRequested={cellValue.isSubRequested}
                status={cellValue.paymentStatus}
                documentType={cellValue.documentType}
                useBds
            />
        </div>
    );
});

export const BillAccountingStatusCell = memo(function BillAccountingStatusCell(
    params: BillCellParams,
) {
    const cellValue = params.data?.purchaseOrderPaymentAccountingStatus;
    if (!cellValue) {
        return null;
    }
    return (
        <span data-testid={testId(params)}>
            <BillAccountingStatusTag status={cellValue} />
        </span>
    );
});

export const BillDocumentTypeCell = memo(function BillDocumentTypeCell(params: BillCellParams) {
    const cellValue = params.data?.documentType;
    if (!cellValue) {
        return null;
    }
    return (
        <span data-testid={testId(params)}>
            <BTStatusBadge
                testid="documentTypeTag"
                text={cellValue}
                displayType={cellValue === "Receipt" ? "info" : "default"}
                textOnly
            />
        </span>
    );
});

export const makeLienWaiverStatusCell = (useAbsoluteLink: boolean) =>
    memo(function LienWaiverStatusCell(params: BillCellParams) {
        const cellValue = params.data?.lienWaiverStatus;
        if (!cellValue || cellValue.status === null) {
            return null;
        }
        const billLink = routes.bill.getDetailsLink(
            cellValue.billId,
            cellValue.jobId,
            "lienWaiver",
        );
        return (
            <div className="flex items-center h-full">
                <RelatedItemTag
                    to={billLink}
                    isToFullUrl={useAbsoluteLink}
                    data-testid={testId(params)}
                    itemType={RelatedItemType.BillLienWaiver}
                    subject={cellValue.title}
                    status={<LienWaiverStatusTag status={cellValue.status} />}
                />
            </div>
        );
    });

export const RelatedPurchaseOrderLinksCell = memo(function RelatedPurchaseOrderLinksCell(
    params: BillCellParams,
) {
    const cellValue = params.data?.relatedPurchaseOrders;
    if (!cellValue) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center h-full" data-testid={testId(params)}>
            {cellValue.map((po) => (
                <RelatedItemTag
                    key={po.id}
                    data-testid={`${testId(params)}_${po.id}`}
                    itemType={RelatedItemType.PurchaseOrder}
                    to={routes.purchaseOrder.getDetailsPath(po.id, po.jobId)}
                    subject={po.title}
                    useMultiTagMargins
                />
            ))}
        </div>
    );
});

export const LinkedScheduleItemStatusCell = memo(function LinkedScheduleItemStatusCell(
    params: BillCellParams,
) {
    const cellValue = params.data?.linkedScheduleItemStatus;
    if (!cellValue) {
        return null;
    }
    return (
        <div className="flex items-center h-full" data-testid={testId(params)}>
            <LinkedScheduleItemStatusTag status={cellValue} />
        </div>
    );
});

export const ApproversCell = memo(function ApproversCell(params: BillCellParams) {
    const cellValue = params.data?.approvers;
    if (!cellValue || !cellValue.approvers || cellValue.approvers.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-wrap gap-1 py-1">
            {cellValue.approvers.map((approver, index) => (
                <BTStatusBadge
                    key={index}
                    testid={`badge-${approver.approverName}-${index}`}
                    text={approver.approverName}
                    displayType={getApproverStatusDisplayType(approver.status)}
                    icon={getApproverStatusIcon(approver.status)}
                />
            ))}
        </div>
    );
});

export const DueDateCell = memo(function DueDateCell(params: BillCellParams) {
    const cellValue = params.data?.dueDate;
    if (!cellValue) {
        return null;
    }
    return (
        <DueDate
            truncateText
            date={cellValue.date ? moment(cellValue.date) : undefined}
            linkedScheduleInfo={cellValue.linkedScheduleInfo}
            isPastDue={cellValue.isPastDue}
            displayTime={cellValue.displayTime}
        />
    );
});

/** Reads either costCodes or varianceCodes (both `select` columns holding string arrays). */
export const SelectCell = memo(function SelectCell(params: BillCellParams) {
    const field = params.colDef?.field as "costCodes" | "varianceCodes" | undefined;
    const cellValue: string[] | null = field ? (params.data?.[field] ?? null) : null;
    if (!cellValue) {
        return null;
    }
    return <span data-testid={testId(params)}>{cellValue.join("\r\n")}</span>;
});

export const RelatedOwnerInvoicesCell = memo(function RelatedOwnerInvoicesCell(
    params: BillCellParams,
) {
    const cellValue = params.data?.relatedOwnerInvoices;
    if (!cellValue) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center h-full" data-testid={testId(params)}>
            {cellValue.map((oi) => (
                <RelatedItemTag
                    key={oi.id}
                    data-testid={`${testId(params)}_${oi.id}`}
                    itemType={RelatedItemType.CustomerInvoice}
                    to={routes.ownerInvoice.getDetailsPath(oi.id, oi.jobId ?? 0)}
                    subject={oi.title}
                    useMultiTagMargins
                />
            ))}
        </div>
    );
});
